/**
 * Users list screen (DNKADM-0162; DNKADM-0255 account-state badge).
 *
 * The route stays `/members`, but the list now reads the tenant's login roster from
 * `GET /v2/admin/users` and labels the surface as Users. Rows with a linked contact
 * ids drill into the existing member detail route; login-only rows do not navigate.
 *
 * **This roster is customers only, by construction (DNKADM-0367).** Staff logins are
 * never returned here — they live under `/v2/admin/platform-members` / Team & access.
 * dink-api does not emit `kind` on these rows at all; it is the client-side constant
 * `'CUSTOMER'` (see `UserRow.kind` and `parseUserRow` in `api/users.ts`), which is why
 * nothing on this roster parses a wire `kind` value — a future "helpful" restoration
 * of a `parseRosterKind` read here would be wrong, and is exactly what the
 * stray-wire-kind regression test in `api/users.test.ts` guards against. There is
 * therefore no staff-visibility control on this screen; the DNKADM-0345 "Show staff"
 * toggle it used to carry was retired because its premise moved to another endpoint,
 * not because filtering staff out of a roster was ever the wrong idea.
 *
 * The "Account state" column is driven by `accountStateBadge`
 * (`./accountState.ts`), a precedence over the server-computed `claimed` signal +
 * membership `status` + `membershipState`: an explicit unclaimed guest stub
 * (`claimed === false`) shows "Unclaimed" — never "Login only" — ahead of
 * DISABLED/INVITED and the membership labels. `claimed: true` renders nothing by
 * itself; it only falls through to the membership-derived labels.
 *
 * **Email unconfirmed (DNKADM-0410).** A row with a login (`claimed === true`) whose
 * `emailVerified` is explicitly `false` gets a separate "Email unconfirmed" chip
 * beside that badge; the account-state label itself is unchanged. No roster filter:
 * the screen's tabs are server-side membership-state filters, not a filter row.
 *
 * **Unclaimed-row invite (DNKADM-0268).** Rows gated on `user.claimed === false`
 * (the DNKADM-0261 signal) AND a non-null email carry an Invite action — or, when a
 * PENDING invitation already exists for the email, "Invited {date}" + Resend. It
 * reuses the DNKADM-0168 wrappers (`inviteUser`/`resendUserInvitation`/
 * `listUserInvitations`) untouched; the FE never calls saas-auth `/internal`. The
 * pending list is loaded LAZILY (only when the roster actually contains an
 * invitable row) and joined by lowercased email. The per-row send is ONE reusable
 * function (`sendRowInvite`) so a future bulk "invite all unclaimed" can loop a
 * selection over the same call (bulk-ready shape; no bulk UI in this unit).
 *
 * **Toolbar, sort and URL state (DNKADM-0430 P1-A).** The tab, the search text, the
 * roster and Invited sorts, and the Prospects page live in the URL (`usersListView.ts`
 * documents the params), so a link or a reload reproduces the view. The roster and
 * Invited lists are whole client-side lists: they search and sort locally through the
 * shared `ListToolbar`/`SortableHeader`. Prospects is server paged: its toolbar drives the
 * existing server search and it has no sort (server sort waits on DNKAPI-0615).
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconAction } from '@/components/IconAction';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { parsePage, toPageParam, useListUrlState } from '@/components/list/useListUrlState';
import { roleLimitedMessage } from '@/components/accessCopy';
import {
  cancelUserInvitation,
  healMemberContact,
  inviteUser,
  listUserInvitations,
  listUsers,
  reconcileInvitation,
  resendUserInvitation,
  type UserInvitation,
  type UserRow,
  type UsersStateFilter,
} from '@/api/users';
import {
  listProspects,
  refreshProspects,
  type Prospect,
  type SyncState,
} from '@/api/prospects';
import { useTheme } from '@mui/material/styles';
import { useCurrentOperator } from '@/auth/authHooks';
import { useToast } from '@/components/useToast';
import { useResource } from '@/hooks/useResource';
import {
  avatarFor,
  chipSx,
  Icon,
  inkAlpha,
  initialsOf,
  MATERIAL_SYMBOLS_FONT,
  resolveMaps,
  resolveTokens,
} from '@/theme';
import { rosterRowKey } from '@/domain/rosterKind';
import { accountStateBadge, showsEmailUnconfirmed, type BadgeStyle } from './accountState';
// The role badge is shared with Settings → General since DNKADM-0304 U9.
import { roleBadge } from './roleBadge';
import { MemberSearchDialog, type PickedContact } from './MemberSearchDialog';
import { WalkInSaleDialog } from './WalkInSaleDialog';
import {
  toProspectsErrorView,
  toResyncContactErrorView,
  toUserInviteErrorView,
} from './membersError';
import {
  INVITED_DEFAULT_SORT,
  INVITED_SORT_KEYS,
  invitedMatches,
  invitedSortValue,
  parseUsersTab,
  ROSTER_DEFAULT_SORT,
  ROSTER_SORT_KEYS,
  rosterMatches,
  rosterSortValue,
  rosterStateOf,
  USERS_TAB_PARAM,
  USERS_URL_DEFAULTS,
  type InvitedSortKey,
  type RosterSortKey,
  type UsersTab,
} from './usersListView';
import { ScreenError } from '@/components/ScreenError';
import { useCalendarZone } from '@/location/useCalendarZone';
import { DATE_ONLY, DATE_TIME, formatInstantMs, instantMs } from '@/utils/instantFormat';

const SKELETON_ROWS = 6;
const PLACEHOLDER = '—';

/**
 * Post-send client cooldown on a row's Invite/Resend control (DNKADM-0268) — with
 * the in-flight disable, three rapid clicks issue ONE request, not three emails.
 */
const INVITE_COOLDOWN_MS = 15_000;

/**
 * The `NOT_CONFIGURED` heal outcome (DNKADM-0204): the tenant has no active CRM
 * integration, so there is nothing to link. Informational, NOT an error — and
 * deliberately brand-neutral (the source guide names the CRM vendor; we never do).
 */
const NO_CRM_INTEGRATION_MESSAGE =
  'This tenant has no CRM integration, so there’s nothing to link.';

/**
 * Format an ISO instant as a short absolute date on the venue's clock (DNKADM-0476 F-05), or the
 * placeholder when absent/unparseable.
 */
function formatDate(iso: string | null, timeZone: string | null): string {
  const ms = instantMs(iso);
  return ms === null ? PLACEHOLDER : formatInstantMs(ms, timeZone, DATE_ONLY);
}

/**
 * The screen-level feedback banner. `severity` distinguishes an invite/heal success
 * from the informational `NOT_CONFIGURED` outcome and a failed heal; `retryUserId` is
 * set only for the retryable `502`, which renders a "Try again" affordance.
 */
interface UsersFeedback {
  severity: 'success' | 'info' | 'error';
  message: string;
  retryUserId: string | null;
}

/**
 * The full tab strip, Prospects FIRST. Prospects is admin-only, so it is filtered out
 * of the rendered strip for a non-admin (see {@link MembersList}); the roster tabs and
 * the Invited tab are always shown.
 */
const USER_TABS: Array<{ label: string; value: UsersTab }> = [
  { label: 'Prospects', value: 'PROSPECTS' },
  { label: 'All Users', value: 'ALL' },
  { label: 'Active Members', value: 'ACTIVE' },
  { label: 'Inactive Members', value: 'INACTIVE' },
  { label: 'Invited Users', value: 'INVITED' },
];

/** Debounce window (ms) for the Prospects server-side search-as-you-type. */
const PROSPECTS_SEARCH_DEBOUNCE_MS = 250;
/** Prospects page size (server-clamped); the pager is 0-indexed on both sides. */
const PROSPECTS_PAGE_SIZE = 25;
/** Modest, bounded poll cadence while a manual Prospects sync is in flight. */
const PROSPECTS_POLL_INTERVAL_MS = 1500;
/** Hard cap on sync polls so a stuck `SYNCING` never loops forever (~30s). */
const PROSPECTS_MAX_SYNC_POLLS = 20;

/** Resolve after `ms` — the modest gap between sync polls. */
function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Format an ISO instant as a short absolute date+time on the venue's clock, or the placeholder. */
function formatSyncTime(iso: string | null, timeZone: string | null): string {
  const ms = instantMs(iso);
  return ms === null ? PLACEHOLDER : formatInstantMs(ms, timeZone, DATE_TIME);
}

/**
 * Debounce a fast-changing value (the Prospects search box) — returns the value only
 * after `delayMs` of quiet so search-as-you-type issues at most one request per pause.
 * Mirrors the local primitive in `MemberSearchDialog` (no shared debounce util exists).
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function MembersList() {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const navigate = useNavigate();
  const showToast = useToast();
  // The "Resync CRM" row action AND the Prospects tab are both admin-only (the same
  // gate as RequireAdmin); the /v2/admin endpoints are the real authority.
  const isAdmin = useCurrentOperator()?.isAdmin === true;
  const canResync = isAdmin;
  // DNKADM-0430: tab, search and sort come from the URL (see `usersListView.ts`).
  const url = useListUrlState(USERS_URL_DEFAULTS);
  const setUrl = url.set;
  const tab = parseUsersTab(url.values.tab, isAdmin);
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, ROSTER_SORT_KEYS, ROSTER_DEFAULT_SORT);
  // The roster state follows the roster tab. On Invited/Prospects it keeps the last roster
  // state, so switching to/from them never refetches the roster (adjust-during-render).
  const rosterTab = rosterStateOf(tab);
  const [state, setState] = useState<UsersStateFilter>(rosterTab ?? 'ALL');
  if (rosterTab !== null && rosterTab !== state) setState(rosterTab);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [feedback, setFeedback] = useState<UsersFeedback | null>(null);
  // The row whose CRM resync is in flight (DNKADM-0204) — one at a time.
  const [resyncingId, setResyncingId] = useState<string | null>(null);
  // Bumped after an invite so the Invited Users tab refetches its pending list.
  const [invitedRefreshKey, setInvitedRefreshKey] = useState(0);

  const users = useResource(
    useCallback(() => listUsers(state), [state]),
    [state],
  );
  const refetchUsers = users.refetch;

  // --- Unclaimed-row invite (DNKADM-0268) ---------------------------------------
  // Loaded LAZILY: only when the roster actually contains an unclaimed row with an
  // email does the pending-invitations join fire (most rosters have none). A load
  // failure degrades to "Invite" everywhere — the 409 ALREADY_PENDING flip below
  // still converges a stale row to the Invited/Resend state.
  const hasInvitableRows = useMemo(
    () => (users.data ?? []).some((u) => u.claimed === false && u.email !== null),
    [users.data],
  );
  const pendingInvitations = useResource(
    useCallback(
      () => (hasInvitableRows ? listUserInvitations('PENDING') : Promise.resolve([])),
      [hasInvitableRows],
    ),
    [hasInvitableRows, invitedRefreshKey],
  );
  const pendingByEmail = useMemo(() => {
    const map = new Map<string, UserInvitation>();
    for (const invitation of pendingInvitations.data ?? []) {
      if (invitation.email !== null && invitation.invitationId !== '') {
        map.set(invitation.email.toLowerCase(), invitation);
      }
    }
    return map;
  }, [pendingInvitations.data]);

  // Overlay of invitations sent from a row THIS session (mirrors the Prospects
  // "Invited" overlay), the single in-flight row, and the post-send cooldown set.
  const [rowInvites, setRowInvites] = useState<Map<string, UserInvitation>>(new Map());
  const [rowInviteBusyId, setRowInviteBusyId] = useState<string | null>(null);
  const [rowCooldowns, setRowCooldowns] = useState<Set<string>>(new Set());
  const cooldownTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(
    () => () => {
      cooldownTimers.current.forEach(clearTimeout);
    },
    [],
  );

  const startInviteCooldown = useCallback((userId: string) => {
    setRowCooldowns((prev) => new Set(prev).add(userId));
    cooldownTimers.current.push(
      setTimeout(() => {
        setRowCooldowns((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }, INVITE_COOLDOWN_MS),
    );
  }, []);

  // The ONE reusable per-row invite call (bulk-ready by design, DNKADM-0268: a
  // future "invite all unclaimed" loops a selection over this same function).
  // Reuses the DNKADM-0168 wrapper untouched — never saas-auth /internal.
  const sendRowInvite = useCallback(
    (user: UserRow) => {
      if (user.email === null) return;
      const email = user.email;
      setRowInviteBusyId(user.userId);
      void (async () => {
        try {
          const invitation = await inviteUser({
            email,
            ...(user.name !== null ? { name: user.name } : {}),
            ...(user.contactId !== null ? { contactId: user.contactId } : {}),
          });
          setRowInvites((prev) => new Map(prev).set(user.userId, invitation));
          startInviteCooldown(user.userId);
          setInvitedRefreshKey((k) => k + 1);
          showToast('Invitation sent.');
        } catch (err) {
          const view = toUserInviteErrorView(err);
          if (view.code === 'INVITATION_ALREADY_PENDING') {
            // Raced a pre-existing pending invite — treat as already-invited: the
            // refetch flips the row to Invited/Resend; no error is surfaced.
            setInvitedRefreshKey((k) => k + 1);
            showToast('An invitation is already pending for this email.');
          } else {
            showToast(view.message, 'error');
            if (view.refreshList) setInvitedRefreshKey((k) => k + 1);
          }
        } finally {
          setRowInviteBusyId(null);
        }
      })();
    },
    [showToast, startInviteCooldown],
  );

  const resendRowInvite = useCallback(
    (user: UserRow, invitationId: string) => {
      setRowInviteBusyId(user.userId);
      void (async () => {
        try {
          const invitation = await resendUserInvitation(invitationId);
          setRowInvites((prev) => new Map(prev).set(user.userId, invitation));
          startInviteCooldown(user.userId);
          setInvitedRefreshKey((k) => k + 1);
          showToast('Invitation resent.');
        } catch (err) {
          const view = toUserInviteErrorView(err);
          showToast(view.message, 'error');
          if (view.refreshList) setInvitedRefreshKey((k) => k + 1);
        } finally {
          setRowInviteBusyId(null);
        }
      })();
    },
    [showToast, startInviteCooldown],
  );

  const openMember = useCallback(
    (contactId: string) => navigate(`/members/${encodeURIComponent(contactId)}`),
    [navigate],
  );

  // Admin-only repair for an orphaned (contact-less) login. The server refetch — not a
  // local row patch — is what re-renders the row with its chevron, so the derived
  // `membershipState` stays the server's view (healing grants a CONTACT, not a membership).
  const resyncContact = useCallback(
    (userId: string) => {
      setResyncingId(userId);
      setFeedback(null);
      void (async () => {
        try {
          const result = await healMemberContact(userId);
          if (result.status === 'NOT_CONFIGURED') {
            setFeedback({ severity: 'info', message: NO_CRM_INTEGRATION_MESSAGE, retryUserId: null });
            return;
          }
          // HEALED and VERIFIED are handled identically. An unknown future status
          // refetches without claiming a link — the server's view is the authority.
          if (result.status === 'HEALED' || result.status === 'VERIFIED') {
            showToast('CRM contact linked.');
          }
          refetchUsers();
        } catch (err) {
          const view = toResyncContactErrorView(err);
          setFeedback({
            severity: 'error',
            message: view.message,
            retryUserId: view.retryable ? userId : null,
          });
          if (view.refreshList) refetchUsers();
        } finally {
          setResyncingId(null);
        }
      })();
    },
    [refetchUsers, showToast],
  );

  // The roster tabs drive `listUsers(state)`; the Invited and Prospects tabs render
  // their own self-contained panels and leave `state` untouched, so switching to/from
  // them never refetches the roster. Each tab has its own columns, so a tab change
  // clears the search, sort and page in the same URL write.
  const onTabChange = useCallback(
    (value: UsersTab) => {
      setUrl({ tab: USERS_TAB_PARAM[value], q: null, sort: null, dir: null, page: null });
    },
    [setUrl],
  );

  // Prospects is admin-only: a non-admin never sees the tab (the strip starts at All
  // Users). The initially-selected tab stays 'ALL' — Prospects is only first in order.
  const visibleTabs = useMemo(
    () => (isAdmin ? USER_TABS : USER_TABS.filter((t) => t.value !== 'PROSPECTS')),
    [isAdmin],
  );

  const loaded = useMemo(() => users.data ?? [], [users.data]);

  const rows = useMemo(
    () =>
      sortRows(
        loaded.filter((user) => rosterMatches(user, search)),
        (user) => rosterSortValue(user, sort.key),
        sort.dir,
        (user) => user.userId,
      ),
    [loaded, search, sort.key, sort.dir],
  );

  const onSearch = useCallback((value: string) => setUrl({ q: value }), [setUrl]);
  const onClearSearch = useCallback(() => setUrl({ q: null }), [setUrl]);
  const onSort = useCallback(
    (key: RosterSortKey) => setUrl(toSortParams(toggleSort(sort, key), ROSTER_DEFAULT_SORT)),
    [setUrl, sort],
  );

  const isInvited = tab === 'INVITED';
  const isProspects = tab === 'PROSPECTS';
  // Set only on the retryable heal failure (502) — drives the banner's retry affordance.
  const retryResyncId = feedback?.retryUserId ?? null;

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          mb: 0.5,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h1" component="h1">
            Users
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 680 }}>
            Search by name or email, then open linked members from the roster.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          {!isInvited && !isProspects && users.status === 'ready' ? (
            <Typography variant="body2" noWrap>
              {loaded.length} user{loaded.length === 1 ? '' : 's'}
            </Typography>
          ) : null}
          <Button
            variant="outlined"
            startIcon={<Icon name="point_of_sale" />}
            onClick={() => setWalkInOpen(true)}
          >
            Sell to a walk-in
          </Button>
          <Button
            variant="contained"
            startIcon={<Icon name="person_add" />}
            onClick={() => setInviteOpen(true)}
          >
            Invite user
          </Button>
        </Stack>
      </Stack>

      <Paper elevation={0} sx={{ mt: 2, bgcolor: 'transparent' }}>
        <Tabs
          value={tab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          onChange={(_, value: UsersTab) => onTabChange(value)}
          aria-label="User membership state"
          sx={{
            minHeight: 42,
            '& .MuiTabs-indicator': { bgcolor: t.brand },
          }}
        >
          {visibleTabs.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} sx={{ minHeight: 42 }} />
          ))}
        </Tabs>
      </Paper>

      {feedback ? (
        <Alert
          severity={feedback.severity}
          role={feedback.severity === 'error' ? 'alert' : 'status'}
          onClose={() => setFeedback(null)}
          action={
            retryResyncId !== null ? (
              <Button color="inherit" size="small" onClick={() => resyncContact(retryResyncId)}>
                Try again
              </Button>
            ) : undefined
          }
          sx={{ mt: 2 }}
        >
          {feedback.message}
        </Alert>
      ) : null}

      {isProspects ? (
        <ProspectsPanel />
      ) : isInvited ? (
        <InvitedUsersPanel
          refreshKey={invitedRefreshKey}
          // A panel cancel/resend must also reconcile the All Users roster's
          // pending-invitation join — its "Invited {date} / Resend" affordance
          // reads the same invitations and kept offering a CANCELLED
          // invitation until a manual page refresh (DNKADM-0304 U3).
          onChanged={() => setInvitedRefreshKey((k) => k + 1)}
        />
      ) : (
        <>
          <ListToolbar
            search={{
              value: search,
              onChange: onSearch,
              placeholder: 'Search users',
              disabled: users.status !== 'ready',
            }}
            onClear={onClearSearch}
          />

          {users.status === 'error' ? (
            <ScreenError
              variant="panel"
              error={users.error}
              what="users"
              onRetry={users.refetch}
              forbiddenMessage={roleLimitedMessage('This screen', 'OWNER, SUPER_ADMIN, or ADMIN')}
              title="Something went wrong"
              retryMessage="The users data could not be loaded. Please try again."
              preferServerMessage
              retryLabel="Try again"
              sx={{ mt: 1 }}
            />
          ) : (
            <Paper elevation={1} sx={{ overflow: 'hidden' }}>
              <TableContainer>
                <Table aria-label="Users">
                  <TableHead>
                    <TableRow>
                      <SortableHeader label="Name" col="name" sort={sort} onSort={onSort} />
                      <SortableHeader label="Email" col="email" sort={sort} onSort={onSort} />
                      <SortableHeader label="Role" col="role" sort={sort} onSort={onSort} />
                      <SortableHeader
                        label="Account state"
                        col="state"
                        sort={sort}
                        onSort={onSort}
                      />
                      <TableCell sx={{ width: 44 }} aria-hidden />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.status === 'loading'
                      ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                          <SkeletonRow key={`sk-${i}`} />
                        ))
                      : rows.map((user) => {
                          // DNKADM-0268 gate: the action EXISTS only for an explicit
                          // unclaimed stub (claimed === false) with an email — never
                          // for claimed/unknown rows, never disabled-with-explanation.
                          const inviteEligible =
                            user.claimed === false && user.email !== null;
                          // The session overlay leads, but its object is parsed
                          // from the TRIMMED write response (no createdAt), so it
                          // is reconciled with the fetched row — "Invited {date}"
                          // renders immediately, and a resend keeps the original
                          // invite date (DNKADM-0304 U1).
                          const rowOverlay = rowInvites.get(user.userId) ?? null;
                          const fetchedInvitation =
                            user.email !== null
                              ? (pendingByEmail.get(user.email.toLowerCase()) ?? null)
                              : null;
                          const pendingInvitation = inviteEligible
                            ? rowOverlay !== null
                              ? reconcileInvitation(rowOverlay, fetchedInvitation)
                              : fetchedInvitation
                            : null;
                          return (
                            <UserTableRow
                              // `rosterRowKey` still typechecks with `kind` fixed to
                              // 'CUSTOMER' (DNKADM-0367) — kept unchanged rather than
                              // simplified to a bare `userId`, out of this unit's scope.
                              key={rosterRowKey(user)}
                              user={user}
                              onOpen={openMember}
                              canResync={canResync}
                              resyncing={resyncingId === user.userId}
                              onResync={resyncContact}
                              inviteEligible={inviteEligible}
                              pendingInvitation={pendingInvitation}
                              inviteBusy={rowInviteBusyId === user.userId}
                              inviteCooling={rowCooldowns.has(user.userId)}
                              onInvite={sendRowInvite}
                              onResendInvite={resendRowInvite}
                            />
                          );
                        })}
                  </TableBody>
                </Table>
              </TableContainer>

              {users.status === 'ready' && rows.length === 0 && (
                <EmptyState
                  search={search}
                  hasUsers={loaded.length > 0}
                  onClearSearch={onClearSearch}
                />
              )}
            </Paper>
          )}
        </>
      )}

      {inviteOpen ? (
        <InviteUserDialog
          onClose={() => setInviteOpen(false)}
          onInvited={(invitation) => {
            setInviteOpen(false);
            setInvitedRefreshKey((k) => k + 1);
            setFeedback({
              severity: 'success',
              message: invitation.email
                ? `Invitation sent to ${invitation.email}.`
                : 'Invitation sent.',
              retryUserId: null,
            });
          }}
        />
      ) : null}

      {walkInOpen && (
        <WalkInSaleDialog
          open
          onClose={() => setWalkInOpen(false)}
          onSold={() => {
            setInvitedRefreshKey((k) => k + 1);
            refetchUsers();
          }}
        />
      )}
    </Box>
  );
}

/**
 * "Invite user" — no OWNER gate here, and no client gate at all: the Users screen is an
 * Operations route, NOT `RequireAdmin`-gated (`src/router.tsx` routes `members` beside
 * Reservations and Activity), so the `/v2/admin/users/invitations` endpoint is the only
 * authority and a refusal surfaces inline. DNKADM-0249 corrected the earlier claim that
 * "the screen is already admin-gated"; DNKADM-0168's shipped "any admin, no OWNER gate"
 * intent is unchanged. Collects an email (required), an optional name, and an optional booking
 * contact to link on acceptance. Submits to `inviteUser` (the server forces the role
 * to `USER`); a BLANK optional field resolves to `undefined` (trim-then-omit) so an
 * empty value is never wired. Business errors surface inline via
 * `toUserInviteErrorView`; on success the Invited Users tab refreshes.
 */
function InviteUserDialog({
  onClose,
  onInvited,
}: {
  onClose: () => void;
  onInvited: (invitation: UserInvitation) => void;
}) {
  const ids = useId();
  const titleId = `${ids}-title`;
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [linkedContact, setLinkedContact] = useState<PickedContact | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  async function handleSubmit() {
    setInlineError(null);
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0) {
      setInlineError('Enter an email address to send an invitation.');
      return;
    }
    const trimmedName = name.trim();
    setSubmitting(true);
    try {
      // Blank optional fields are OMITTED (undefined), never sent as '' — the API
      // wires `name`/the contact link only when the value is `!== undefined`.
      const invitation = await inviteUser({
        email: trimmedEmail,
        ...(trimmedName.length > 0 ? { name: trimmedName } : {}),
        ...(linkedContact !== null ? { contactId: linkedContact.contactId } : {}),
      });
      onInvited(invitation);
    } catch (err) {
      setInlineError(toUserInviteErrorView(err).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onClose={submitting ? undefined : onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id={titleId}>Invite user</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            We’ll email them an invitation to create a login for this tenant. You can optionally
            link them to an existing member so their bookings connect on acceptance.
          </Typography>
          <TextField
            label="Email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            fullWidth
            slotProps={{ htmlInput: { 'aria-label': 'Email' } }}
          />
          <TextField
            label="Name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
            fullWidth
            slotProps={{ htmlInput: { 'aria-label': 'Name' } }}
          />
          <Stack spacing={1}>
            <Typography
              component="div"
              variant="body2"
              sx={{ color: 'text.secondary' }}
            >
              Link to member (optional)
            </Typography>
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {linkedContact !== null ? (
                <Chip
                  icon={<Icon name="person" sx={{ fontSize: 18 }} />}
                  label={`Linked: ${linkedContact.name}`}
                  onDelete={submitting ? undefined : () => setLinkedContact(null)}
                  deleteIcon={
                    <Box
                      component="span"
                      role="button"
                      aria-label="Clear linked member"
                      sx={{ fontFamily: MATERIAL_SYMBOLS_FONT, fontFeatureSettings: "'liga'" }}
                    >
                      close
                    </Box>
                  }
                />
              ) : null}
              <Button
                id={`${ids}-link-member`}
                variant="outlined"
                startIcon={<Icon name="person_search" sx={{ fontSize: 18 }} />}
                onClick={() => setPickerOpen(true)}
                disabled={submitting}
              >
                {linkedContact === null ? 'Choose member' : 'Change member'}
              </Button>
            </Stack>
          </Stack>
          {inlineError ? (
            <Alert severity="error" role="alert">
              {inlineError}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Sending…' : 'Send invite'}
        </Button>
      </DialogActions>
      {pickerOpen ? (
        <MemberSearchDialog
          title="Link a member"
          onClose={() => setPickerOpen(false)}
          onPick={setLinkedContact}
        />
      ) : null}
    </Dialog>
  );
}

/**
 * The Invited Users tab — pending USER invitations from `listUserInvitations('PENDING')`
 * (already USER-only server-side, no client filter). Any admin may Cancel (removes the
 * row) or Resend (replaces the row with the fresh invitation the endpoint returns). The
 * removed/replaced overlays keep those edits without an effect-driven copy of the fetched
 * list; a write that reports the target moved on (`refreshList`) refetches instead. The
 * `refreshKey` bump from a new invite re-runs the fetch. A successful cancel/resend
 * reports up via `onChanged` (DNKADM-0304 U3) so the parent bumps the SAME key — the
 * roster's pending-invitation join re-reads and its "Invited/Resend" affordance can't
 * keep pointing at a cancelled invitation.
 */
function InvitedUsersPanel({
  refreshKey,
  onChanged,
}: {
  refreshKey: number;
  onChanged?: () => void;
}) {
  const calendarZone = useCalendarZone();
  const theme = useTheme();
  const invites = useResource(
    useCallback(() => listUserInvitations('PENDING'), []),
    [refreshKey],
  );
  // DNKADM-0430: search and sort live in the URL (cleared by the parent's tab change).
  const url = useListUrlState(USERS_URL_DEFAULTS);
  const setUrl = url.set;
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, INVITED_SORT_KEYS, INVITED_DEFAULT_SORT);
  const onSort = useCallback(
    (key: InvitedSortKey) => setUrl(toSortParams(toggleSort(sort, key), INVITED_DEFAULT_SORT)),
    [setUrl, sort],
  );
  // Row-local edits layered over the fetched list: cancelled ids drop out; resent ids
  // render their fresh invitation. Keyed by the ORIGINAL invitation id so a resend
  // (which may rotate the id) still matches its row.
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [replaced, setReplaced] = useState<Map<string, UserInvitation>>(new Map());
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const visible = useMemo(() => {
    return (invites.data ?? [])
      .filter((inv) => !removed.has(inv.invitationId))
      .map((inv) => ({ key: inv.invitationId, inv: replaced.get(inv.invitationId) ?? inv }));
  }, [invites.data, removed, replaced]);
  const rows = useMemo(
    () =>
      sortRows(
        visible.filter(({ inv }) => invitedMatches(inv, search)),
        ({ inv }) => invitedSortValue(inv, sort.key),
        sort.dir,
        ({ key }) => key,
      ),
    [visible, search, sort.key, sort.dir],
  );

  async function handleCancel(key: string, invitationId: string) {
    setInlineError(null);
    setBusyId(key);
    try {
      await cancelUserInvitation(invitationId);
      setRemoved((prev) => new Set(prev).add(key));
      onChanged?.();
    } catch (err) {
      const view = toUserInviteErrorView(err);
      setInlineError(view.message);
      if (view.refreshList) invites.refetch();
    } finally {
      setBusyId(null);
    }
  }

  async function handleResend(key: string, invitationId: string) {
    setInlineError(null);
    setBusyId(key);
    try {
      const fresh = await resendUserInvitation(invitationId);
      setReplaced((prev) => new Map(prev).set(key, fresh));
      onChanged?.();
    } catch (err) {
      const view = toUserInviteErrorView(err);
      setInlineError(view.message);
      if (view.refreshList) invites.refetch();
    } finally {
      setBusyId(null);
    }
  }

  // DNKADM-0433/0440: a 403 is "not allowed", not a network failure - no Try again.
  if (invites.status === 'error') {
    return (
      <ScreenError
        variant="panel"
        error={invites.error}
        what="invitations"
        onRetry={invites.refetch}
        retryMessage="We couldn’t reach the pending invitations. Please try again."
        retryLabel="Try again"
        sx={{ mt: 2 }}
      />
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {inlineError ? (
        <Alert severity="error" role="alert" onClose={() => setInlineError(null)} sx={{ mb: 2 }}>
          {inlineError}
        </Alert>
      ) : null}
      <ListToolbar
        search={{
          value: search,
          onChange: (value) => setUrl({ q: value }),
          placeholder: 'Search invitations',
          disabled: invites.status !== 'ready',
        }}
        onClear={() => setUrl({ q: null })}
      />
      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table aria-label="Invited users">
            <TableHead>
              <TableRow>
                <SortableHeader label="Email" col="email" sort={sort} onSort={onSort} />
                <SortableHeader label="Invited" col="invited" sort={sort} onSort={onSort} />
                <SortableHeader label="Expires" col="expires" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.status === 'loading'
                ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                    <InvitedSkeletonRow key={`sk-${i}`} />
                  ))
                : rows.map(({ key, inv }) => (
                    <TableRow key={key} hover>
                      <TableCell sx={{ color: inkAlpha(theme, 0.7) }}>
                        {inv.email || PLACEHOLDER}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(inv.createdAt, calendarZone)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatDate(inv.expiresAt, calendarZone)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busyId === key}
                            onClick={() => void handleResend(key, inv.invitationId)}
                          >
                            Resend
                          </Button>
                          <Button
                            size="small"
                            color="inherit"
                            disabled={busyId === key}
                            onClick={() => void handleCancel(key, inv.invitationId)}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        {invites.status === 'ready' && rows.length === 0 && visible.length > 0 ? (
          <Box sx={{ p: 7, textAlign: 'center' }}>
            <Icon name="person_search" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
            <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
              No invitations match “{search}”
            </Typography>
          </Box>
        ) : null}
        {invites.status === 'ready' && visible.length === 0 ? (
          <Box sx={{ p: 7, textAlign: 'center' }}>
            <Icon name="mail" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
            <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
              No pending invitations
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 360, mx: 'auto' }}>
              Invitations you send will appear here until they’re accepted.
            </Typography>
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}

/**
 * The Prospects tab (DNKADM-0205 U2) — the tenant's CRM-only contacts who are not yet
 * members. Self-contained like {@link InvitedUsersPanel}: it owns its OWN resource
 * (`listProspects`) and never touches the roster. Server-side debounced search + a
 * 0-indexed pager (both sides 0-indexed — deliberately NOT routed through
 * `toWirePage`/`toUiPage`, per the U1 delivery note), a `sync.state`-driven header with
 * an inline Re-sync link (DNKADM-0206) that POSTs `refreshProspects` then polls until
 * the sync leaves `SYNCING`,
 * and a per-row Invite that reuses `inviteUser` (the prospect STAYS listed after an
 * invite — it hasn't converted — and is marked "Invited" so it can't be re-invited).
 */
function ProspectsPanel() {
  const calendarZone = useCalendarZone();
  const showToast = useToast();
  // DNKADM-0430: the search text and page live in the URL. The URL `page` is 1-indexed;
  // this panel's `page` stays 0-indexed (first page is 0), as is the wire (U1).
  const url = useListUrlState(USERS_URL_DEFAULTS);
  const setUrl = url.set;
  const query = url.values.q;
  const debouncedQuery = useDebouncedValue(query, PROSPECTS_SEARCH_DEBOUNCE_MS);
  const page = parsePage(url.values.page) - 1;
  const setPage = (next: number) => setUrl({ page: toPageParam(next + 1) });
  // Bumped to force a refetch through `useResource` after a manual sync completes.
  const [reloadKey, setReloadKey] = useState(0);
  // True while a manual re-sync (the sync POST + the poll) is in flight.
  const [manualSyncing, setManualSyncing] = useState(false);
  // Contact ids just invited this session — the row stays listed but is marked "Invited".
  const [invited, setInvited] = useState<Set<string>>(new Set());
  const [invitingId, setInvitingId] = useState<string | null>(null);

  // Guard against a setState after the panel unmounts mid-poll (tab switch).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const prospects = useResource(
    useCallback(
      () => listProspects({ query: debouncedQuery, page, size: PROSPECTS_PAGE_SIZE }),
      [debouncedQuery, page],
    ),
    // `reloadKey` drives the post-sync refetch through the resource deps (mirrors
    // InvitedUsersPanel's `refreshKey`); the fetcher itself doesn't close over it.
    [debouncedQuery, page, reloadKey],
  );

  const data = prospects.data;
  const sync = data?.sync ?? { lastSyncedAt: null, nextSyncAt: null, state: '' as SyncState };
  const rows = data?.prospects ?? [];
  const total = data?.total ?? rows.length;
  const size = data?.size ?? PROSPECTS_PAGE_SIZE;
  const syncing = manualSyncing || sync.state === 'SYNCING';

  function handleSearch(value: string) {
    setUrl({ q: value, page: null }); // a new search term restarts at the first page
  }

  // Kick a background sync, then poll the list on a modest bounded interval until the
  // server leaves `SYNCING`, then refetch through `useResource` for the fresh list +
  // timestamps. The re-sync control is disabled for the whole session (`manualSyncing`).
  async function handleRefresh() {
    setManualSyncing(true);
    try {
      await refreshProspects();
      for (let attempt = 0; attempt < PROSPECTS_MAX_SYNC_POLLS; attempt += 1) {
        await wait(PROSPECTS_POLL_INTERVAL_MS);
        if (!mountedRef.current) return;
        const next = await listProspects({
          query: debouncedQuery,
          page,
          size: PROSPECTS_PAGE_SIZE,
        });
        if (next.sync.state !== 'SYNCING') break;
      }
    } catch (err) {
      if (mountedRef.current) showToast(toProspectsErrorView(err).message, 'error');
    } finally {
      if (mountedRef.current) {
        setManualSyncing(false);
        setReloadKey((k) => k + 1);
      }
    }
  }

  // Per-row invite — reuses the existing invite path with the prospect's own
  // `{ email, name, contactId }` (model `contactId`, mapped to the wire field by
  // `inviteUser`). On success the prospect STAYS listed, marked "Invited".
  async function handleInvite(prospect: Prospect) {
    if (prospect.email === null) return;
    setInvitingId(prospect.contactId);
    try {
      // Blank optional fields are OMITTED, never sent as undefined/'' (the invite
      // contract is unchanged; `contactId` is mapped to the wire field by `inviteUser`).
      await inviteUser({
        email: prospect.email,
        ...(prospect.name !== null ? { name: prospect.name } : {}),
        contactId: prospect.contactId,
      });
      setInvited((prev) => new Set(prev).add(prospect.contactId));
      showToast(`Invitation sent to ${prospect.email}.`);
    } catch (err) {
      showToast(toUserInviteErrorView(err).message, 'error');
    } finally {
      setInvitingId(null);
    }
  }

  // A load failure renders through the shared ScreenError (DNKADM-0440). Feature copy on
  // top: the transient 502 says the CRM was unreachable, and a 404 is the tenant-config
  // case (never "no CRM connected" — that is the NOT_CONFIGURED 200, handled as data).
  if (prospects.status === 'error') {
    const view = toProspectsErrorView(prospects.error);
    return (
      <ScreenError
        variant="panel"
        error={prospects.error}
        what="prospects"
        onRetry={prospects.refetch}
        notFound={
          <Alert severity="warning" role="alert" sx={{ mt: 2 }}>
            {view.message}
          </Alert>
        }
        retryMessage={view.retryable ? view.message : 'Please try again in a moment.'}
        retryLabel="Try again"
        sx={{ mt: 2 }}
      />
    );
  }

  // NOT_CONFIGURED is a normal 200 (no CRM connected) — an INFO notice, never an error,
  // and deliberately brand-neutral (say "CRM", never the vendor name).
  if (data !== null && sync.state === 'NOT_CONFIGURED') {
    return (
      <Alert severity="info" role="status" sx={{ mt: 2 }}>
        Connect a CRM to see prospects.
      </Alert>
    );
  }

  const searchActive = debouncedQuery.trim().length > 0;

  return (
    <Box sx={{ mt: 2 }}>
      {data !== null ? (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography variant="body2" role="status" sx={{ color: 'text.secondary' }}>
            {syncing
              ? 'Syncing…'
              : sync.state === 'SYNCED'
                ? `Last synced ${formatSyncTime(sync.lastSyncedAt, calendarZone)} · Next sync ${formatSyncTime(sync.nextSyncAt, calendarZone)}`
                : ''}
          </Typography>
          {/*
           * The manual re-sync trigger (DNKADM-0206): a small inline link hugging the
           * sync line, NOT a far-right outlined button. Behavior is unchanged — same
           * `handleRefresh`, same bounded poll, same `manualSyncing` disabled window.
           */}
          <Link
            component="button"
            type="button"
            variant="body2"
            aria-label="Re-sync prospects"
            aria-busy={manualSyncing}
            disabled={manualSyncing}
            onClick={() => void handleRefresh()}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              '&:disabled': { color: 'text.disabled', cursor: 'default' },
            }}
          >
            <Icon
              name="sync"
              sx={{
                fontSize: 18,
                '@keyframes dinkResyncSpin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
                animation: manualSyncing ? 'dinkResyncSpin 1.2s linear infinite' : 'none',
                // Reduced motion: no spin — the disabled state + the "Syncing…" status
                // line still convey that a re-sync is running.
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            />
            Re-sync
          </Link>
        </Stack>
      ) : null}

      {/* Server paged: the toolbar drives the existing server search; no sort (Phase 2). */}
      <ListToolbar
        search={{ value: query, onChange: handleSearch, placeholder: 'Search prospects' }}
        onClear={() => handleSearch('')}
      />

      <Paper elevation={1} sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table aria-label="Prospects">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {prospects.status === 'loading'
                ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                    <ProspectSkeletonRow key={`sk-${i}`} />
                  ))
                : rows.map((prospect) => (
                    <ProspectTableRow
                      key={prospect.contactId}
                      prospect={prospect}
                      invited={invited.has(prospect.contactId)}
                      inviting={invitingId === prospect.contactId}
                      onInvite={handleInvite}
                    />
                  ))}
            </TableBody>
          </Table>
        </TableContainer>

        {prospects.status === 'ready' && rows.length === 0 ? (
          <ProspectsEmptyState searchActive={searchActive} query={debouncedQuery} state={sync.state} />
        ) : null}

        {rows.length > 0 ? (
          <ProspectsPagination
            page={page}
            total={total}
            size={size}
            loading={prospects.status === 'loading'}
            onPrev={() => setPage(Math.max(0, page - 1))}
            onNext={() => setPage(page + 1)}
          />
        ) : null}
      </Paper>
    </Box>
  );
}

/** One prospect row: name / email / phone / last-updated + the per-row Invite action. */
function ProspectTableRow({
  prospect,
  invited,
  inviting,
  onInvite,
}: {
  prospect: Prospect;
  invited: boolean;
  inviting: boolean;
  onInvite: (prospect: Prospect) => void;
}) {
  const calendarZone = useCalendarZone();
  const theme = useTheme();
  const maps = resolveMaps(theme);
  return (
    <TableRow hover>
      <TableCell sx={{ fontWeight: 500 }}>{prospect.name ?? PLACEHOLDER}</TableCell>
      <TableCell sx={{ color: inkAlpha(theme, 0.7) }}>{prospect.email ?? PLACEHOLDER}</TableCell>
      <TableCell sx={{ color: inkAlpha(theme, 0.7) }}>{prospect.phone ?? PLACEHOLDER}</TableCell>
      <TableCell>
        <Typography variant="body2">{formatDate(prospect.dateUpdated, calendarZone)}</Typography>
      </TableCell>
      <TableCell align="right">
        {invited ? (
          <Chip
            size="small"
            label="Invited"
            sx={{ bgcolor: maps.greenTints.chip, color: maps.status.active.fg }}
          />
        ) : prospect.invitable && prospect.email !== null ? (
          <Button
            size="small"
            variant="outlined"
            disabled={inviting}
            onClick={() => onInvite(prospect)}
          >
            {inviting ? 'Inviting…' : 'Invite'}
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function ProspectSkeletonRow() {
  return (
    <TableRow>
      <TableCell>
        <Skeleton width={130} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={180} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={110} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={90} height={12} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={72} height={30} sx={{ ml: 'auto' }} />
      </TableCell>
    </TableRow>
  );
}

/**
 * The Prospects empty state. A search with no matches is distinct from a genuinely
 * empty list, whose message is driven by `sync.state`: `NEVER_SYNCED`/`SYNCING` are
 * still catching up; a `SYNCED` empty list simply has none yet.
 */
function ProspectsEmptyState({
  searchActive,
  query,
  state,
}: {
  searchActive: boolean;
  query: string;
  state: SyncState;
}) {
  const theme = useTheme();
  if (searchActive) {
    return (
      <Box sx={{ p: 7, textAlign: 'center' }}>
        <Icon name="person_search" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
          No prospects match “{query}”
        </Typography>
      </Box>
    );
  }
  const message =
    state === 'NEVER_SYNCED' || state === 'SYNCING'
      ? 'Syncing your CRM, check back'
      : 'No prospects yet';
  return (
    <Box sx={{ p: 7, textAlign: 'center' }}>
      <Icon name="group" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>{message}</Typography>
    </Box>
  );
}

/** 0-indexed pager for the Prospects table, driven by `total`/`size` (not `toUiPage`). */
function ProspectsPagination({
  page,
  total,
  size,
  loading,
  onPrev,
  onNext,
}: {
  page: number;
  total: number;
  size: number;
  loading: boolean;
  onPrev: () => void;
  onNext: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const totalPages = size > 0 ? Math.max(1, Math.ceil(total / size)) : 1;
  const atLastPage = page + 1 >= totalPages;
  return (
    <Stack
      component="nav"
      aria-label="Prospects pagination"
      direction="row"
      sx={{
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1,
        px: 2,
        py: 1.5,
        borderTop: `1px solid ${t.lineFaint}`,
      }}
    >
      <Typography variant="body2" aria-current="page">
        Page {page + 1}
      </Typography>
      <IconAction label="Previous page" size="small" onClick={onPrev} disabled={page <= 0 || loading}>
        <Icon name="chevron_left" />
      </IconAction>
      <IconAction label="Next page" size="small" onClick={onNext} disabled={atLastPage || loading}>
        <Icon name="chevron_right" />
      </IconAction>
    </Stack>
  );
}

function InvitedSkeletonRow() {
  return (
    <TableRow>
      <TableCell>
        <Skeleton width={180} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={90} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={90} height={12} />
      </TableCell>
      <TableCell align="right">
        <Skeleton width={120} height={32} sx={{ ml: 'auto' }} />
      </TableCell>
    </TableRow>
  );
}

function SkeletonRow() {
  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
          <Skeleton variant="circular" width={34} height={34} />
          <Skeleton width={130} height={12} />
        </Stack>
      </TableCell>
      <TableCell>
        <Skeleton width={180} height={12} />
      </TableCell>
      <TableCell>
        <Skeleton width={66} height={20} />
      </TableCell>
      <TableCell>
        <Skeleton width={108} height={20} />
      </TableCell>
      <TableCell />
    </TableRow>
  );
}

/**
 * One roster row. A row with a linked contact navigates and shows the chevron; a
 * contact-less (orphaned) row shows the admin-only "Resync CRM" repair in that same
 * action cell (DNKADM-0204) and stays non-navigable until the heal lands.
 *
 * DNKADM-0268: `inviteEligible` (claimed === false + email, computed by the parent)
 * adds the Invite / "Invited {date}"+Resend control beside the Account-state badge,
 * reusing the DNKADM-0168 wrappers via the parent's handlers. Never rendered for
 * claimed/unknown/email-less rows.
 */
function UserTableRow({
  user,
  onOpen,
  canResync,
  resyncing,
  onResync,
  inviteEligible,
  pendingInvitation,
  inviteBusy,
  inviteCooling,
  onInvite,
  onResendInvite,
}: {
  user: UserRow;
  onOpen: (contactId: string) => void;
  canResync: boolean;
  resyncing: boolean;
  onResync: (userId: string) => void;
  inviteEligible: boolean;
  pendingInvitation: UserInvitation | null;
  inviteBusy: boolean;
  inviteCooling: boolean;
  onInvite: (user: UserRow) => void;
  onResendInvite: (user: UserRow, invitationId: string) => void;
}) {
  const calendarZone = useCalendarZone();
  const theme = useTheme();
  const t = resolveTokens(theme);
  const clickable = user.contactId !== null;
  const displayName = user.name ?? user.userId;
  const avatar = avatarFor(user.userId, theme.palette.mode);
  const role = roleBadge(user.role, theme.palette.mode);
  const accountState = accountStateBadge(user, theme.palette.mode);
  const openLabel = `Open ${displayName}`;

  return (
    <TableRow
      hover={clickable}
      onClick={clickable ? () => onOpen(user.contactId as string) : undefined}
      sx={{ cursor: clickable ? 'pointer' : 'default' }}
    >
      <TableCell>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{ width: 34, height: 34, fontSize: 13, bgcolor: avatar.bg, color: avatar.fg }}
          >
            {initialsOf(displayName)}
          </Avatar>
          <Typography sx={{ fontWeight: 500 }}>{displayName}</Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ color: inkAlpha(theme, 0.7) }}>{user.email ?? PLACEHOLDER}</TableCell>
      <TableCell>
        {/* A row the wire gave neither a role nor a `kind` is UNKNOWN, not a user
            (DNKADM-0360): `rosterDisplayRole` returns '' and that renders as the
            placeholder — never an empty chip, and never invented as "User". Same guard
            the platform-members roster applies. */}
        {user.role ? <BadgeChip badge={role} /> : PLACEHOLDER}
      </TableCell>
      <TableCell>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <BadgeChip badge={accountState} />
          {/* DNKADM-0410: a separate badge beside the account state, never a
              replacement for it — see `showsEmailUnconfirmed`. */}
          {showsEmailUnconfirmed(user) ? (
            <Tooltip title="Hasn’t confirmed their email address yet">
              <Chip size="small" color="warning" variant="outlined" label="Email unconfirmed" />
            </Tooltip>
          ) : null}
          {inviteEligible ? (
            pendingInvitation !== null ? (
              <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  Invited {formatDate(pendingInvitation.createdAt, calendarZone)}
                </Typography>
                <Button
                  size="small"
                  disabled={inviteBusy || inviteCooling}
                  aria-label={`Resend invitation to ${displayName}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onResendInvite(user, pendingInvitation.invitationId);
                  }}
                >
                  {inviteBusy ? 'Resending…' : 'Resend'}
                </Button>
              </Stack>
            ) : (
              <Button
                size="small"
                variant="outlined"
                disabled={inviteBusy || inviteCooling}
                aria-label={`Invite ${displayName}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onInvite(user);
                }}
              >
                {inviteBusy ? 'Inviting…' : 'Invite'}
              </Button>
            )
          ) : null}
        </Stack>
      </TableCell>
      <TableCell align="center" sx={{ color: clickable ? t.textGhost : 'inherit' }}>
        {clickable ? (
          <IconAction
            size="small"
            label={openLabel}
            onClick={(event) => {
              event.stopPropagation();
              onOpen(user.contactId as string);
            }}
          >
            <Icon name="chevron_right" />
          </IconAction>
        ) : canResync ? (
          <Button
            size="small"
            variant="outlined"
            disabled={resyncing}
            onClick={() => onResync(user.userId)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {resyncing ? 'Resyncing…' : 'Resync CRM'}
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function BadgeChip({ badge }: { badge: BadgeStyle }) {
  const chip = <Chip size="small" label={badge.label} sx={chipSx(badge)} />;
  if (badge.hint) return <Tooltip title={badge.hint}>{chip}</Tooltip>;
  return chip;
}

function EmptyState({
  search,
  hasUsers,
  onClearSearch,
}: {
  search: string;
  hasUsers: boolean;
  onClearSearch: () => void;
}) {
  const theme = useTheme();
  if (search.trim().length > 0 && hasUsers) {
    return (
      <Box sx={{ p: 7, textAlign: 'center' }}>
        <Icon name="person_search" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>
          No users match “{search}”
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 340, mx: 'auto' }}>
          Check the spelling, or try part of an email.
        </Typography>
        <Button variant="outlined" color="primary" sx={{ mt: 2 }} onClick={onClearSearch}>
          Clear search
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 7, textAlign: 'center' }}>
      <Icon name="group" sx={{ fontSize: 46, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 16, fontWeight: 500, mt: 1 }}>No users found.</Typography>
    </Box>
  );
}
